import { Box } from "@mui/material";
import type {
	CellClassParams,
	CellStyle,
	ColDef,
	GetRowIdParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { globalDefaultColDef, mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	GRID_PROPERTIES,
	type GridPropertyDef,
	type GridPropertyFormat,
	type StandRow,
} from "../models/TrendDisplay";
import { PROPERTY_COLUMN_WIDTH } from "./mill-visualizer/utils/viewBoxLayout";

interface TrendGridRow {
	property: string;
	propertyKey: string;
	description: string;
	format: GridPropertyFormat;
	legendColor?: string;
	propMeta: GridPropertyDef;
	[standName: string]: StandRow | GridPropertyDef | string | undefined;
}

const formatCellValue = (
	value: number | boolean | null | undefined,
	format: GridPropertyFormat,
): string => {
	if (value === null || value === undefined) return "--------";
	if (format === "BOOL") return value === true ? "Yes" : "No";
	if (format === "INT") return String(Math.round(Number(value)));
	return Number(value).toFixed(3);
};

interface CellHighlight {
	background?: string;
	color?: string;
	fontWeight?: number;
}

const getCellHighlight = (
	stand: StandRow,
	prop: GridPropertyDef,
): CellHighlight => {
	// Inactive stand → grey across all rows
	if (stand.Active !== true) {
		return { background: "#D6D4D4", color: "#1e293b" };
	}

	// Actual R-Factor out of [Min, Max] when comparison is enabled
	if (
		prop.key === "actual" &&
		stand.EnabledComparison === true &&
		stand.RFactorCurrent != null &&
		stand.RFactorMin != null &&
		stand.RFactorMax != null &&
		(stand.RFactorCurrent < stand.RFactorMin ||
			stand.RFactorCurrent > stand.RFactorMax)
	) {
		return { color: mesfColors.error.main, fontWeight: 700 };
	}

	// Pass Count over PassMaxCount
	if (
		prop.key === "passCount" &&
		stand.PassCount != null &&
		stand.PassMaxCount != null &&
		stand.PassCount > stand.PassMaxCount
	) {
		return { color: mesfColors.error.main, fontWeight: 700 };
	}

	// Billet Present = true
	if (prop.key === "billetPresent" && stand.IsBilletPresent === true) {
		return { background: mesfColors.success.main, color: "#fff" };
	}

	return {};
};

const cellStyleCb = (params: CellClassParams<TrendGridRow>): CellStyle => {
	const stand = params.value as StandRow | undefined;
	const prop = params.data?.propMeta;
	const base: CellStyle = {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		overflow: "hidden",
		textOverflow: "ellipsis",
	};
	if (!stand || !prop) return base;
	const highlight = getCellHighlight(stand, prop);
	return {
		...base,
		...(highlight.background ? { backgroundColor: highlight.background } : {}),
		...(highlight.color ? { color: highlight.color } : {}),
		...(highlight.fontWeight ? { fontWeight: highlight.fontWeight } : {}),
	};
};

const cellValueFormatter = (params: {
	value: unknown;
	data?: TrendGridRow;
}) => {
	const stand = params.value as StandRow | undefined;
	const prop = params.data?.propMeta;
	if (!stand || !prop) return "";
	// Inactive stand → blank every cell (parity with legacy IHBox, which
	// rewrites every value to "notShow" client-side and renders it as
	// "--------"). The grey background from getCellHighlight already
	// communicates "this stand is off"; showing stale numbers on top of
	// that is misleading.
	if (stand.Active !== true) return "--------";
	const raw = stand[prop.standField] as number | boolean | null | undefined;
	return formatCellValue(raw, prop.format);
};

const PropertyHeaderRenderer = (params: { data?: TrendGridRow }) => {
	const data = params.data;
	if (!data) return null;
	return (
		<Box
			title={data.description}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.75,
				width: "100%",
			}}
		>
			{data.legendColor ? (
				<Box
					component="span"
					sx={{
						width: 10,
						height: 10,
						borderRadius: "50%",
						backgroundColor: data.legendColor,
						border: "1px solid #ACA8A8",
						display: "inline-block",
					}}
				/>
			) : null}
			<Box
				component="span"
				sx={{ fontWeight: 600, fontSize: 12, color: mesfColors.text.primary }}
			>
				{data.property}
			</Box>
		</Box>
	);
};

const buildRowData = (stands: StandRow[]): TrendGridRow[] => {
	return GRID_PROPERTIES.map((prop) => {
		const row: TrendGridRow = {
			property: prop.label,
			propertyKey: prop.key,
			description: prop.description,
			format: prop.format,
			legendColor: prop.legendColor,
			propMeta: prop,
		};
		for (const stand of stands) {
			row[stand.Stand] = stand;
		}
		return row;
	});
};

const buildColumnDefs = (standNames: string[]): ColDef<TrendGridRow>[] => {
	const propertyCol: ColDef<TrendGridRow> = {
		field: "property",
		headerName: "Stand",
		pinned: "left",
		width: PROPERTY_COLUMN_WIDTH,
		minWidth: PROPERTY_COLUMN_WIDTH,
		maxWidth: PROPERTY_COLUMN_WIDTH,
		suppressMovable: true,
		sortable: false,
		filter: false,
		resizable: false,
		cellRenderer: PropertyHeaderRenderer,
		cellStyle: { paddingLeft: 8 },
		headerStyle: { fontWeight: 700, fontSize: 13 },
	};

	const standCols: ColDef<TrendGridRow>[] = standNames.map((name) => ({
		colId: name,
		headerName: name,
		valueGetter: (params) => params.data?.[name] as StandRow | undefined,
		valueFormatter: cellValueFormatter,
		flex: 1,
		minWidth: 70,
		sortable: false,
		filter: false,
		resizable: false,
		cellStyle: cellStyleCb,
		headerStyle: { fontWeight: 700, fontSize: 13 },
		wrapText: false,
		autoHeight: false,
	}));

	return [propertyCol, ...standCols];
};

const defaultColDef: ColDef = {
	...globalDefaultColDef,
	resizable: false,
};

const getRowId = (params: GetRowIdParams<TrendGridRow>) =>
	params.data.propertyKey;

interface TrendGridProps {
	stands: StandRow[];
}

export const TrendGrid = ({ stands }: TrendGridProps) => {
	const rowData = useMemo(() => buildRowData(stands), [stands]);

	const namesKey = stands.map((s) => s.Stand).join(",");
	const colDefs = useMemo(
		() => buildColumnDefs(stands.map((s) => s.Stand)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[namesKey],
	);

	if (stands.length === 0) return null;

	return (
		<AgGridReact<TrendGridRow>
			rowData={rowData}
			columnDefs={colDefs}
			defaultColDef={defaultColDef}
			domLayout="autoHeight"
			headerHeight={28}
			rowHeight={28}
			getRowId={getRowId}
			suppressCellFocus
			suppressContextMenu
			suppressColumnVirtualisation
			suppressHorizontalScroll
			suppressRowHoverHighlight
			animateRows={false}
		/>
	);
};
